import { useState, useRef, useEffect, useMemo } from 'react';
import { IonIcon } from '@ionic/react';
import { search, add } from 'ionicons/icons';
import { TRADE_SKILLS } from '@/lib/validation/registration';

interface SkillAutocompleteProps {
  existingSkills: string[];
  onSelect?: (name: string, isCustom: boolean) => void;
  placeholder?: string;
}

export function SkillAutocomplete({ existingSkills, onSelect, placeholder = 'Search skills…' }: SkillAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return TRADE_SKILLS.filter(
      (s) => s.toLowerCase().includes(q) && !existingSkills.includes(s),
    ).slice(0, 8);
  }, [query, existingSkills]);

  const showCustom = query.trim().length > 0 && !filtered.some((s) => s.toLowerCase() === query.toLowerCase());

  useEffect(() => {
    setHighlightIdx(-1);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSelect = (name: string, isCustom: boolean) => {
    onSelect?.(name, isCustom);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const items = showCustom ? [...filtered, query.trim()] : filtered;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && highlightIdx < items.length) {
        const selected = items[highlightIdx];
        handleSelect(selected, highlightIdx === filtered.length && showCustom);
      } else if (showCustom && query.trim()) {
        handleSelect(query.trim(), true);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <IonIcon icon={search} style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            fontSize: '1rem', color: 'var(--bp-text-muted, #64748b)',
          }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              width: '100%', padding: '8px 12px 8px 32px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, color: 'var(--bp-text, #f1f5f9)',
              fontSize: '0.85rem', outline: 'none',
            }}
          />
        </div>
      </div>

      {open && (filtered.length > 0 || showCustom) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
          marginTop: 4, background: 'var(--bp-surface, #1e293b)',
          borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          maxHeight: 240, overflowY: 'auto',
        }}>
          {filtered.map((skill, i) => (
            <button
              key={skill}
              type="button"
              onClick={() => handleSelect(skill, false)}
              style={{
                display: 'block', width: '100%', padding: '8px 12px',
                background: i === highlightIdx ? 'rgba(59,130,246,0.15)' : 'none',
                border: 'none', color: 'var(--bp-text, #f1f5f9)',
                fontSize: '0.8rem', cursor: 'pointer', textAlign: 'left',
              }}
            >
              {skill}
            </button>
          ))}
          {showCustom && (
            <button
              type="button"
              onClick={() => handleSelect(query.trim(), true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                width: '100%', padding: '8px 12px',
                background: highlightIdx === filtered.length ? 'rgba(59,130,246,0.15)' : 'none',
                border: 'none', color: 'var(--bp-blue-400, #60a5fa)',
                fontSize: '0.8rem', cursor: 'pointer', textAlign: 'left',
              }}
            >
              <IonIcon icon={add} style={{ fontSize: '0.9rem' }} />
              Add "{query.trim()}" as custom skill
            </button>
          )}
        </div>
      )}
    </div>
  );
}
